import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignAPI } from '../services/api';
import SkillsInput from '../components/common/SkillsInput';
import {
  apiErrorText, cleanExperienceInput, EXPERIENCE_HINT, EXPERIENCE_MAX_LENGTH, experienceFromLevel, parseExperience,
} from '../utils/experience';

const EditCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [skillsChanged, setSkillsChanged] = useState(false);
  const [originalSkills, setOriginalSkills] = useState([]);
  const [originalRole, setOriginalRole] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    job_role: '',
    job_description: '',
    experience_years: '',
    required_skills: [],
    language: 'en',
    difficulty: 'medium',
  });

  const loadCampaign = useCallback(async () => {
    try {
      setLoadingCampaign(true);
      const response = await campaignAPI.get(id);
      const campaign = response.data;
      if (!campaign.is_active) {
        navigate(`/admin/campaigns/${id}`);
        return;
      }
      const experienceYears =
        campaign.experience_years != null && campaign.experience_years !== ''
          ? String(campaign.experience_years)
          : experienceFromLevel(campaign.experience_level);

      const data = {
        name: campaign.name || '',
        job_role: campaign.job_role || '',
        job_description: campaign.job_description || '',
        experience_years: experienceYears,
        required_skills: campaign.required_skills || [],
        language: campaign.language || 'en',
        difficulty: (campaign.difficulty_level || 'medium').toLowerCase(),
      };
      setFormData(data);
      setOriginalSkills(campaign.required_skills || []);
      setOriginalRole(campaign.job_role || '');
    } catch (error) {
      setError('Failed to load position');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoadingCampaign(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  // Detect if role or skills changed
  useEffect(() => {
    const roleChanged = formData.job_role !== originalRole;
    const skillsStr = JSON.stringify(
      [...(formData.required_skills || [])].sort()
    );
    const origStr = JSON.stringify(
      [...originalSkills].sort()
    );
    setSkillsChanged(roleChanged || skillsStr !== origStr);
  }, [formData.job_role, formData.required_skills,
      originalRole, originalSkills]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        name: formData.name,
        job_role: formData.job_role,
        job_description: formData.job_description || null,
        required_skills: formData.required_skills,
        language: formData.language || 'en',
        difficulty_level: formData.difficulty || 'medium',
      };

      const experience = parseExperience(formData.experience_years);
      if (experience.error) {
        setError(`Experience: ${experience.error}`);
        return;
      }
      if (experience.label) {
        payload.experience_years = experience.label;
      }

      await campaignAPI.update(id, payload);
      setSaved(true);
      setTimeout(() => {
        navigate(`/admin/campaigns/${id}`);
      }, 1500);
    } catch (err) {
      setError(apiErrorText(err, 'Failed to update position'));
    } finally {
      setLoading(false);
    }
  };

  const editExperience = parseExperience(formData.experience_years);

  if (loadingCampaign) {
    return (
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        color: '#94A3B8',
      }}>
        Loading position...
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 13,
    color: '#111827',
    outline: 'none',
    background: 'white',
    fontFamily: 'inherit',
    transition: 'border-color .15s',
  };

  return (
    <div style={{
      flex: 1,
      overflowY: 'auto',
      padding: '28px 32px',
      background: '#F9FAFB',
    }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>

        {/* Back button */}
        <button
          onClick={() => navigate(`/admin/campaigns/${id}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: '#6B7280',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: '5px 0',
            marginBottom: 20,
          }}
        >
          ← Back to Position
        </button>

        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{
            fontSize: 22,
            fontWeight: 700,
            color: '#111827',
            marginBottom: 4,
          }}>
            Edit Position
          </h1>
          <p style={{ fontSize: 13, color: '#6B7280' }}>
            Update position details.
            Manage questions from the Position page.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#991B1B',
          }}>
            {error}
          </div>
        )}

        {/* Skills/Role changed warning */}
        {skillsChanged && (
          <div style={{
            background: '#FFFBEB',
            border: '1px solid #F59E0B',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13,
            color: '#92400E',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                Role or skills changed
              </div>
              <div style={{ fontSize: 12, color: '#B45309' }}>
                Your existing questions may be outdated.
                After saving, go to the
                <strong> Questions tab</strong> to
                regenerate them.
              </div>
            </div>
          </div>
        )}

        {/* Form card */}
        <div style={{
          background: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: 12,
          padding: '28px',
          boxShadow: '0 1px 4px rgba(0,0,0,.06)',
        }}>

          {/* Position Name */}
          <div style={{ marginBottom: 18 }}>
            <label style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}>
              Position Name <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Senior Developer Q1 2024"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#6366F1'}
              onBlur={e => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {/* Job Role */}
          <div style={{ marginBottom: 18 }}>
            <label style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}>
              Job Role <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              name="job_role"
              value={formData.job_role}
              onChange={handleInputChange}
              placeholder="e.g., Senior Software Engineer"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#6366F1'}
              onBlur={e => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {/* Experience + Language */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
            marginBottom: 18,
          }}>
            <div>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#374151',
                display: 'block',
                marginBottom: 6,
              }}>
                Experience (years)
              </label>
              <input
                name="experience_years"
                value={formData.experience_years}
                onChange={e => setFormData(prev => ({ ...prev, experience_years: cleanExperienceInput(e.target.value) }))}
                maxLength={EXPERIENCE_MAX_LENGTH}
                placeholder="e.g. 3, 2-3 or 10+"
                aria-invalid={!!editExperience.error}
                style={{ ...inputStyle, ...(editExperience.error ? { borderColor: '#DC2626' } : {}) }}
                onFocus={e => e.target.style.borderColor = '#6366F1'}
                onBlur={e => {
                  e.target.style.borderColor = editExperience.error ? '#DC2626' : '#E5E7EB';
                  if (editExperience.label) setFormData(prev => ({ ...prev, experience_years: editExperience.label }));
                }}
              />
              <div style={{ fontSize: 11, marginTop: 4, color: editExperience.error ? '#DC2626' : '#6B7280' }}>
                {editExperience.error || EXPERIENCE_HINT}
              </div>
            </div>
            <div>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#374151',
                display: 'block',
                marginBottom: 6,
              }}>
                Interview Language
              </label>
              <select
                value={formData.language}
                onChange={e => setFormData(f => ({
                  ...f, language: e.target.value
                }))}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                }}
                onFocus={e => e.target.style.borderColor = '#6366F1'}
                onBlur={e => e.target.style.borderColor = '#E5E7EB'}
              >
                <option value="en">English</option>
                <option value="es">Spanish (Bilingual)</option>
              </select>
            </div>
          </div>

          {/* Job Description */}
          <div style={{ marginBottom: 18 }}>
            <label style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}>
              Job Description
            </label>
            <textarea
              name="job_description"
              value={formData.job_description}
              onChange={handleInputChange}
              rows={3}
              placeholder="Describe the role and requirements..."
              style={{
                ...inputStyle,
                resize: 'vertical',
                lineHeight: 1.5,
              }}
              onFocus={e => e.target.style.borderColor = '#6366F1'}
              onBlur={e => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {/* Required Skills */}
          <div style={{ marginBottom: 24 }}>
            <label style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}>
              Required Skills
            </label>
            <SkillsInput
              value={formData.required_skills || []}
              onChange={skills => setFormData(f => ({
                ...f, required_skills: skills
              }))}
              placeholder="Type a skill to add..."
            />
          </div>

          {/* Interview config — difficulty + hire score (needed for Craft JD / synced campaigns) */}
          <div style={{
            marginBottom: 24,
            paddingTop: 8,
            borderTop: '1px solid #F3F4F6',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 14 }}>
              Interview configuration
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#374151',
                display: 'block',
                marginBottom: 6,
              }}>
                Difficulty Level
              </label>
              <select
                value={formData.difficulty}
                onChange={e => setFormData(f => ({ ...f, difficulty: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
                onFocus={e => e.target.style.borderColor = '#6366F1'}
                onBlur={e => e.target.style.borderColor = '#E5E7EB'}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="adaptive">Adaptive</option>
              </select>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Used when generating or regenerating questions. Existing questions keep their current difficulty until regenerated.
              </div>
            </div>

            <div style={{ marginBottom: 8, fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
              Grading strictness and hire score are set per batch. Open the interview on the
              position page and use a batch’s evaluation settings.
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSubmit}
            disabled={loading || saved}
            style={{
              width: '100%',
              padding: '11px',
              borderRadius: 9,
              border: 'none',
              background: saved
                ? '#10B981'
                : '#6366F1',
              color: 'white',
              fontSize: 14,
              fontWeight: 700,
              cursor: loading || saved
                ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'background .3s',
            }}
          >
            {loading
              ? 'Saving...'
              : saved
                ? '✓ Saved — redirecting...'
                : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCampaign;
